import {
    Injectable,
    ComponentFactoryResolver,
    ApplicationRef,
    Injector,
    EmbeddedViewRef,
    ComponentRef,
    EventEmitter,
    ElementRef,
} from "@angular/core";
import { EdpToasterModule as ToasterModule } from "./edp-toaster.module";
import { EdpToastItemComponent as ToastItemComponent } from "./edp-toast-item/edp-toast-item.component";
import { EdpToastContainerComponent as ToastContainerComponent } from "./edp-toast-container/edp-toast-container.component";
import { EdpToastType } from "./edp-toast-type";

export interface EdpToast {
    /**
     * Get toast configuration
     */
    getConfiguration(): EdpToastConfig;

    /**
     * Set toast type (this will influence the stylesheet of the toast)
     *
     * @param type Type of toast
     */
    setType(type: EdpToastType): EdpToast;

    /**
     * Get toast message
     */
    getMessage(): string;

    /**
     * Set toast message (can contain HTML)
     *
     * @param message Message of the toast
     */
    setMessage(message: string): EdpToast;

    /**
     * Set toast message (will encode any HTML sepcial caracters)
     *
     * @param message Message of the toast
     */
    setMessageTxt(message: string): EdpToast;

    /**
     * Set toast target (default is body)
     *
     * @param target Target
     */
    setTarget(el: HTMLElement): EdpToast;

    /**
     * Set wait timeout before closing the toast.
     * -1 or false will disable it
     *
     * @param timeout Number of seconds the toast will be visable
     * -1 means until user closes it
     * @param reset Reset any running timeout. Default is true. If
     * false and there is a time running, only the internal variable
     * is changed
     */
    setTimeout(timeout: number | boolean, reset?: boolean): EdpToast;

    /**
     * Returns true if the toast is visible or false otherwise.
     */
    getVisibility(): boolean;

    /**
     * Switches the "do not goup messages" flag.
     *
     * @param group If true (the default), message toasts will not be
     * grouped
     */
    dontGroup(dontGroup?: boolean): EdpToast;

    /**
     * Get DOM element of the toast
     */
    getDOMEelement(): HTMLElement;

    /**
     * Displays the toast element inside the target DOM
     * element (inside body by default).
     *
     * @param messageOrPromise Optional text for the toast or a Promise.
     * If a Promise is supplied the toast will only be visible after the Promise
     * has resolved (or rejected). The Promise resolve value will be used to set
     * the toast text (if is a string), but if the Promise rejects, then the type
     * of the Promise changes to Error and the error message is used to set the
     * toast text.
     * @returns Will allways return a Promise that will resolve as soon as the
     * toast is closed. The returned Promise will never reject.
     */
    show(messageOrPromise?: string | Promise<any>): Promise<EdpToast>;

    /**
     * Close the toast
     */
    close(event?: Event): EdpToast;

    /**
     * Registers a handler to be called when the toast closes.
     *
     * @param callback Handler to be called when the toast is closed
     * @param prepend Optional boolean indicating if the handler should
     * be prepended to the list of existing handlers. If false (default)
     * the handler will be appended.
     */
    onClose(callback: (toast?: EdpToast) => any, prepend?: boolean): EdpToast;

    /**
     * Registers a handler to be called when the toast closes.
     *
     * @param callback Handler to be called when the toast is displayed
     * on the screen
     * @param prepend Optional boolean indicating if the handler should
     * be prepended to the list of existing handlers. If false (default)
     * the handler will be appended.
     */
    onShow(callback: (toast?: EdpToast) => any, prepend?: boolean): EdpToast;
}

export interface EdpToastConfig {
    /**
     * Target HTML element where you whant to place the Toast.
     * The default is null, which means place the Toast in the
     * document body inside a toast group container.
     */
    target?: HTMLElement;

    /**
     * Place the Toast directly in the target element (for this
     * to happen the target property must not be null). The default
     * is false, which means place the Toast in the
     * document body inside a toast group container.
     */
    direct?: boolean;

    /**
     * Toast type (jus influences the interface)
     */
    type?: EdpToastType;

    /**
     * Timeout in milliseconds before automaticly
     * closing the Toast (default is -1 - no timeout)
     */
    timeout?: number;

    /**
     * Boolean indicating whether the close button should
     * be visible or not (default is true)
     */
    showCloseButton?: boolean;

    /**
     * Boolean indicating whether the close button should
     * be visible or no (default is true)
     */
    showLeftIcon?: boolean;

    /**
     * Message to be shown in the toast
     */
    message?: string;

    /**
     * Route to be navigate on click button
     */
    route?: string;

    /**
     * CSS classes list to add to the Toast element
     */
    extraCssClasses?: string[];

    showCloseModalButton?: boolean;
    textCloseModalButton?: string;
    textButton?: string;
    // tslint:disable-next-line: ban-types
    functionOnClickClose?: Function;
    functionCloseButton?: Function;
}

/**
 * Protected class that represents a toast element
 * It's protected, because the developer shouldn't
 * be able to create toasts without using the bellow
 * factory. It will work like a Singleton.
 */
class Toast implements EdpToast {
    public static componentFactoryResolver: ComponentFactoryResolver;
    public static appRef: ApplicationRef;
    public static injector: Injector;
    protected static mainContainer: ComponentRef<ToastContainerComponent>;
    protected static mainContainerIsInDOM = false;

    /* Instance Properties */
    protected idInContainer: string;
    protected config: EdpToastConfig;
    protected vPromise: Promise<EdpToast>;
    protected toastItemComponentRef: ComponentRef<ToastItemComponent>;
    protected timeout: any = null;
    protected visible = false;
    protected showEmitter: EventEmitter<Toast> = new EventEmitter(true);
    protected closeEmitter: EventEmitter<Toast> = new EventEmitter(true);

    constructor(config: EdpToastConfig) {
        if (config.direct && !config.target) {
            config.target = document.body;
        } else if (config.target && !config.direct) {
            config.direct = true;
        }

        config.target = document.body;
        this.config = config;
    }

    public static getMainContainer(appendToDOM = false): ComponentRef<ToastContainerComponent> {
        if (!Toast.mainContainer) {
            // get the factory of our ToastContainerComponent (using angular componentFactoryResolver)
            // Creating an instace of the ToastContainerComponent
            const componentRef = Toast.componentFactoryResolver.resolveComponentFactory(ToastContainerComponent).create(Toast.injector);

            // attach the new component to the angular component tree
            Toast.appRef.attachView(componentRef.hostView);

            Toast.mainContainer = componentRef;
        }

        if (appendToDOM && !Toast.mainContainerIsInDOM) {
            // get the root DOM-element of our ToastContainerComponent
            const domElem = (Toast.mainContainer.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;

            // attach element to DOM (usualy document.body)
            document.body.appendChild(domElem);

            Toast.mainContainerIsInDOM = true;
        }

        return Toast.mainContainer;
    }

    public getConfiguration(): EdpToastConfig {
        return this.config;
    }

    public dontGroup(dontGroup = true): EdpToast {
        this.config.direct = dontGroup;
        return this;
    }

    public setType(type: EdpToastType): EdpToast {
        this.config.type = type;
        this.updateInterface();
        return this;
    }

    public getMessage(): string {
        return this.config.message;
    }

    public setMessage(message: string): EdpToast {
        this.config.message = message;
        this.updateInterface();
        return this;
    }

    public setMessageTxt(message: string): EdpToast {
        this.setMessage(
            message
                .replace(/&/g, "&amp;")
                .replace(/>/g, "&gt;")
                .replace(/</g, "&lt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#39;")
        );
        return this;
    }

    public setTarget(target: HTMLElement): EdpToast {
        this.config.target = target;
        return this;
    }

    public setTimeout(timeout: number | boolean, reset: boolean = true): EdpToast {
        this.config.timeout = timeout === false ? -1 : Number(timeout);

        if (this.visible && (!this.timeout || (this.timeout && reset))) {
            if (this.timeout) {
                // there's a timeout already running (we must stop it!)
                window.clearTimeout(this.timeout);
                this.timeout = null;
            }

            if (this.config.timeout > 0) {
                // start a new timeout if there's actually a timeout
                this.timeout = window.setTimeout(
                    this.toastItemComponentRef.instance.onClose.bind(this.toastItemComponentRef.instance),
                    this.config.timeout
                );
            }
        }

        return this;
    }

    public getVisibility(): boolean {
        return this.visible;
    }

    public getDOMEelement(): HTMLElement {
        if (this.toastItemComponentRef) {
            return (this.toastItemComponentRef.hostView as EmbeddedViewRef<any>).rootNodes[0].childNodes[0] as HTMLElement;
        }

        return null;
    }

    public show(messageOrPromise?: string | Promise<any>): Promise<EdpToast> {
        if (this.vPromise) {
            // it's allready visible or was visible
            return this.vPromise;
        }

        let _show = (resolve: any) => {
            this.visible = true;
            this.onClose(resolve, true);

            this.appendToDOM();

            // set timeout for the div
            if (this.config.timeout > 0) {
                this.setTimeout(this.config.timeout);
            }

            this.showEmitter.emit();
        };

        if (messageOrPromise) {
            if (typeof messageOrPromise === "string") {
                // message is a string, set his value as the message of the toast
                this.setMessage(messageOrPromise);
            } else if (messageOrPromise instanceof Promise) {
                // message is a promise, only show the toast when the Promise resolves
                // or rejects
                const __show = _show;
                _show = (resolve: CallableFunction) => {
                    messageOrPromise.then(
                        (val: any) => {
                            if (typeof val === "string") {
                                this.setMessage(val);
                            }

                            __show.call(this, resolve);
                        },
                        (reason: any) => {
                            this.setType(EdpToastType.error);
                            if (reason instanceof Error) {
                                this.setMessage(reason.message);
                            } else if (typeof reason === "string") {
                                this.setMessage(reason);
                            } else {
                                this.setMessage("Unknown error");
                            }

                            __show.call(this, resolve);
                        }
                    );
                };
            }
            // else, just ignore the message parameter
        }

        return (this.vPromise = new Promise<EdpToast>(_show));
    }

    public close(evt?: Event): EdpToast {
        if (this.timeout) {
            clearTimeout(this.timeout);
            this.timeout = null;
        }

        this.removeFromDOM();
        this.visible = false;
        this.closeEmitter.emit();
        return this;
    }

    public onClose(callback: (toast?: EdpToast) => any, prepend = false): EdpToast {
        this.closeEmitter.subscribe(callback.bind(this, this));
        return this;
    }

    public onShow(callback: (toast?: EdpToast) => any, prepend = false): EdpToast {
        this.showEmitter.subscribe(callback.bind(this, this));
        return this;
    }

    /**
     * Create and append toast HTML element to the target DOM element.
     */
    protected async appendToDOM() {
        let componentRef: ComponentRef<ToastItemComponent>;

        if (!this.toastItemComponentRef) {
            // get the factory of our ToastItemComponent (using angular componentFactoryResolver)
            // Creating an instace of the ToastItemComponent
            componentRef = Toast.componentFactoryResolver.resolveComponentFactory(ToastItemComponent).create(Toast.injector);

            // configuring the component
            this.updateInterface(componentRef);

            this.toastItemComponentRef = componentRef;
        } else {
            componentRef = this.toastItemComponentRef;
        }

        if (this.config.direct) {
            // attach the new component to the angular component tree
            Toast.appRef.attachView(componentRef.hostView);

            // get the root DOM-element of our ToastItemComponent
            const domElem = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;

            // attach element to DOM (usualy document.body)
            this.config.target.appendChild(domElem);
        } else {
            // append to container
            // start by creating the container if it doesn't exist
            const container = Toast.getMainContainer(true);

            this.idInContainer = await container.instance.loadChildComponent(componentRef);
        }
    }

    /**
     * Removes the toast HTML element from DOM
     */
    protected removeFromDOM(): void {
        if (!this.config.direct) {
            // first remove from group
            Toast.mainContainer.instance.removeChildComponent(this.idInContainer, false);
        }

        if (this.toastItemComponentRef && this.toastItemComponentRef.hostView) {
            // detach from view
            Toast.appRef.detachView(this.toastItemComponentRef.hostView);
        }

        if (this.toastItemComponentRef && this.toastItemComponentRef.destroy) {
            // destroy element
            this.toastItemComponentRef.destroy();
        }

        this.toastItemComponentRef = null;
    }

    /**
     * Updates the interface after a configuration change
     */
    protected updateInterface(ref?: ComponentRef<ToastItemComponent>) {
        if (!ref && !this.toastItemComponentRef) {
            return;
        } else if (!ref) {
            ref = this.toastItemComponentRef;
        }

        // configuring the component
        ref.instance.isDirect = this.config.direct;
        ref.instance.closeCallback = this.close.bind(this);
        ref.instance.message = this.config.message;
        ref.instance.type = this.config.type;
        ref.instance.showIcon = this.config.showLeftIcon;
        ref.instance.classList = this.config.extraCssClasses;
        ref.instance.showCloseButton = this.config.showCloseButton;
        ref.instance.showCloseModalButton = this.config.showCloseModalButton;
        ref.instance.textCloseModalButton = this.config.textCloseModalButton;
        ref.instance.textButton = this.config.textButton;
        ref.instance.functionOnClickClose = this.config.functionOnClickClose;
        ref.instance.functionCloseButton = this.config.functionCloseButton;

        /* (just ignore - moved to the spec file!)
        if (jasmine) {
            // this line is required in order to force jamin to detect
            // the changes in DOM (using ng serve should be automatic)
            // ref.changeDetectorRef.detectChanges();
        }*/
    }
}

@Injectable({
    providedIn: ToasterModule,
})
export class EdpToasterService {
    protected toastItemComponentRef: ComponentRef<ToastItemComponent>;
    protected defaultConfig: EdpToastConfig = {
        target: null, // document.body,
        direct: false,
        type: EdpToastType.info,
        timeout: -1,
        showCloseButton: true,
        showLeftIcon: true,
        message: "",
        extraCssClasses: [],
        textButton: "OK",
    };

    public constructor(
        protected componentFactoryResolver: ComponentFactoryResolver,
        protected appRef: ApplicationRef,
        protected injector: Injector
    ) {
        Toast.componentFactoryResolver = componentFactoryResolver;
        Toast.appRef = appRef;
        Toast.injector = injector;
    }

    /**
     * This will create a new toast element (an instance of EdpToast interface)
     * Please notice that this method will not show the toast nor even inject any
     * HTML to the DOM. You have to call show on the EdpToast interface to actually
     * display the toast
     */
    public create(message?: string, type?: EdpToastType, config?: EdpToastConfig): EdpToast {
        // cloning and extendig default configuration
        const myConfig: any = {};
        if (message) {
            myConfig.message = message;
        }

        if (type) {
            myConfig.type = type;
        }

        const newConfig = this.extend(true, this.defaultConfig, myConfig, config) as EdpToastConfig;

        return new Toast(newConfig);
    }

    /**
     * This method will change the default configuration that used to
     * create new toasts (will affect new toasts that will be created
     * in the future only)
     *
     * @param config Object containig new configuration properties
     * to overwrite
     */
    public configure(config: EdpToastConfig): void {
        this.defaultConfig = this.extend(true, this.defaultConfig, config);
    }

    /**
     * Get default configuration
     */
    public getConfig(): EdpToastConfig {
        return this.defaultConfig;
    }

    /**
     * Merge the contents of two or more objects together into the first object
     *
     * @param args Variable number of arguments, which can be in one of the
     * following formats:
     * extend( target [, object1 ] [, objectN ] )
     *      target
     *      Type: Object
     *      An object that will receive the new properties if additional objects
     *      are passed  in or that will extend the jQuery namespace if it is the
     *      sole argument.
     *
     *      object1
     *      Type: Object
     *      An object containing additional properties to merge in.
     *
     *      objectN
     *      Type: Object
     *      Additional objects containing properties to merge in.
     * == or ============================================
     * extend( [deep ], target, object1 [, objectN ] )
     *      deep
     *      Type: Boolean
     *      If true, the merge becomes recursive (aka. deep copy). Passing false for this argument is not supported.
     *
     *      target
     *      Type: Object
     *      The object to extend. It will receive the new properties.
     *
     *      object1
     *      Type: Object
     *      An object containing additional properties to merge in.
     *
     *      objectN
     *      Type: Object
     *      Additional objects containing properties to merge in.
     */
    private extend(...args: any[]): any {
        // Variables
        const extended = {};
        const length = args.length;
        let deep = false;
        let i = 0;

        // Check if a deep merge
        if (Object.prototype.toString.call(args[0]) === "[object Boolean]") {
            deep = args[0];
            i++;
        }

        // Merge the object into the extended object
        const merge = (obj: any) => {
            for (const prop in obj) {
                if (Object.prototype.hasOwnProperty.call(obj, prop)) {
                    // If deep merge and property is an object, merge properties
                    if (deep && Object.prototype.toString.call(obj[prop]) === "[object Object]") {
                        extended[prop] = this.extend(true, extended[prop], obj[prop]);
                    } else {
                        extended[prop] = obj[prop];
                    }
                }
            }
        };

        // Loop through each object and conduct a merge
        for (; i < length; i++) {
            merge(args[i]);
        }

        return extended;
    }
}
